import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCreatorDetail } from '../../../hooks/useCreatorDetail';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import CreatorDetailHero from '../../../components/creators-v2/CreatorDetailHero';
import CreatorDetailActivity from '../../../components/creators-v2/CreatorDetailActivity';

/**
 * Admin V2 Creator Detail — Détail d'un créateur (admin scope).
 * Route : /admin/creators/:id (via V2Switcher) + /admin/v2/creators/:id.
 * Feature flag : `pages.creators.detail_admin`.
 */
const AdminV2CreatorDetail = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const { id } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const { creator, currentMonth, dailyRows, activity, loading, error, reload } = useCreatorDetail({
    creatorId: id,
    lookbackDays: 60,
  });

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/v2/creators')}
              className="shrink-0 text-muted-foreground hover:text-foreground text-sm"
              aria-label={t('common.back', 'Back')}
            >
              ←
            </button>
            <div className="min-w-0">
              <Link to="/admin/v2/creators" className="text-xs text-muted-foreground hover:text-foreground">
                {t('creatorsV2.adminTitle', 'Creators')}
              </Link>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground truncate">
                {creator?.display_name || creator?.tiktok_username || '—'}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName}</p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {loading && !creator ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : !creator ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('creatorsV2.detail.notFound', 'Creator not found or not accessible to your tenant.')}
            </div>
          ) : (
            <>
              <CreatorDetailHero creator={creator} currentMonth={currentMonth} role="admin" />
              <CreatorDetailActivity activity={activity} dailyRows={dailyRows} lookbackDays={60} loading={loading} />
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2CreatorDetail;
